import { Modal, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { History, RotateCcw } from 'lucide-react';
import MDEditor from '@uiw/react-md-editor';
import { useAgentRegistry } from '../../../apps/hooks/agent/useAgentRegistry';
import type { AgentPersonaResponse } from '../../../infrastructure/AIAgentRegistryClient';
import { formatDate } from '../../../utils/dateUtils';

interface AgentPersonaHistoryModalProps {
  show: boolean;
  agentId: string | null;
  agentName: string;
  onHide: () => void;
  onRestore?: (version: AgentPersonaResponse) => void;
}

export const AgentPersonaHistoryModal = ({
  show,
  agentId,
  agentName,
  onHide,
  onRestore,
}: AgentPersonaHistoryModalProps) => {
  const { useAgentPersonaHistoryQuery } = useAgentRegistry();
  const { data, isLoading, error } = useAgentPersonaHistoryQuery(agentId ?? '');

  const versions = data?.versions || [];
  const sortedVersions = [...(versions || [])].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          <History size={20} className="me-2" />
          History: {agentName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {isLoading ? (
          <div className="text-center my-5">
            <Spinner animation="border" />
          </div>
        ) : error ? (
          <Alert variant="danger">Failed to load history. Please try again.</Alert>
        ) : !sortedVersions || sortedVersions.length === 0 ? (
          <Alert variant="info">No history available for this agent persona.</Alert>
        ) : (
          <div>
            <div className="mb-3">
              <small className="text-muted">
                Showing {sortedVersions.length} version{sortedVersions.length !== 1 ? 's' : ''} (most recent first)
              </small>
            </div>
            <div className="position-relative">
              {sortedVersions.map((version, index) => (
                <div key={version.id} className="mb-4 position-relative">
                  <div
                    className="d-flex gap-3 align-items-start"
                    style={{
                      paddingLeft: '24px',
                      borderLeft: index !== sortedVersions.length - 1 ? '2px solid #dee2e6' : 'none',
                    }}
                  >
                    <div
                      className="position-absolute bg-white d-flex align-items-center justify-content-center rounded-circle border border-2"
                      style={{
                        left: '-9px',
                        top: '4px',
                        width: '18px',
                        height: '18px',
                        borderColor: '#0d6efd',
                      }}
                    >
                      <div
                        className="rounded-circle"
                        style={{
                          width: '8px',
                          height: '8px',
                          backgroundColor: '#0d6efd',
                        }}
                      />
                    </div>

                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <Badge bg={version.type === 'supervisor' ? 'primary' : 'info'}>{version.type}</Badge>
                        <Badge bg="secondary">{version.category}</Badge>
                        <small className="text-muted">{formatDate(version.updatedAt)}</small>
                        {index === 0 && <Badge bg="success">Current</Badge>}
                      </div>

                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                          <strong>{version.name}</strong>
                        </div>
                        {onRestore && index > 0 && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => onRestore(version)}
                            className="ms-2"
                          >
                            <RotateCcw size={14} className="me-1" />
                            Restore
                          </Button>
                        )}
                      </div>

                      {version.description && <div className="text-muted small mb-2">{version.description}</div>}

                      {version.personaMarkdown && (
                        <div className="mt-3 border rounded p-3 bg-light">
                          <div className="mb-2">
                            <strong>Persona Markdown:</strong>
                          </div>
                          <div data-color-mode="light">
                            <MDEditor.Markdown source={version.personaMarkdown} />
                          </div>
                        </div>
                      )}

                      {version.manualMarkdown && (
                        <div className="mt-3 border rounded p-3 bg-light">
                          <div className="mb-2">
                            <strong>Manual Markdown:</strong>
                          </div>
                          <div data-color-mode="light">
                            <MDEditor.Markdown source={version.manualMarkdown} />
                          </div>
                        </div>
                      )}

                      {version.manualId && (
                        <div className="mt-2">
                          <small className="text-muted">Linked Manual ID: </small>
                          <Badge bg="info">{version.manualId}</Badge>
                        </div>
                      )}

                      {version.parentId && (
                        <div className="mt-2">
                          <small className="text-muted">Parent Supervisor ID: </small>
                          <Badge bg="secondary">{version.parentId}</Badge>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

